import { useCallback, useEffect, useState } from 'react';
import { Toggle } from '../Toggle';
import { createPortal } from 'react-dom';
import {
  Activity,
  BatteryCharging,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Loader2,
  Pencil,
  PlugZap,
  Plus,
  Square,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import {
  createEVCharger,
  deleteEVCharger,
  endEVManualSession,
  getEVCharger,
  getEVChargers,
  sendEVChargerCommand,
  startEVManualSession,
  updateEVCharger,
  updateEVManualSession,
  EVCharger,
  EVChargerCommandName,
  EVChargerConnection,
  EVChargerControlType,
  EVChargerStatus,
} from '../../api/client';

// How often the section refreshes charger live status from the API.
const STATUS_POLL_MS = 20000;

// Share of charger energy that ends up in the pack (AC on-board charger
// losses). Must match CHARGE_EFFICIENCY in shared/utils/ev_target.py.
const CHARGE_EFFICIENCY = 0.9;

/**
 * Approximate usable pack sizes for EVs common in Australia, for owners
 * who don't know their battery size. Usable (not gross) kWh, rounded.
 */
const EV_BATTERY_PRESETS: { label: string; kwh: number }[] = [
  { label: 'BYD Atto 3 (Standard)', kwh: 50 },
  { label: 'BYD Atto 3 (Extended)', kwh: 60 },
  { label: 'BYD Dolphin', kwh: 45 },
  { label: 'BYD Seal (Dynamic)', kwh: 61 },
  { label: 'BYD Seal (Premium / Performance)', kwh: 82 },
  { label: 'BYD Sealion 7', kwh: 82 },
  { label: 'Hyundai Ioniq 5 / Kia EV6 (long range)', kwh: 74 },
  { label: 'Hyundai Kona Electric (long range)', kwh: 64 },
  { label: 'Kia EV5 (long range)', kwh: 88 },
  { label: 'MG4 (Excite 51)', kwh: 50 },
  { label: 'MG4 (64 / 77 kWh)', kwh: 62 },
  { label: 'MG ZS EV (long range)', kwh: 68 },
  { label: 'Nissan Leaf', kwh: 39 },
  { label: 'Nissan Leaf e+', kwh: 59 },
  { label: 'Polestar 2 (long range)', kwh: 79 },
  { label: 'Tesla Model 3 / Y (RWD)', kwh: 57 },
  { label: 'Tesla Model 3 / Y (Long Range / Performance)', kwh: 75 },
  { label: 'Volvo EX30 / XC40 (extended)', kwh: 65 },
];

/** Energy a % target works out to per plug-in, given the battery size and arrival SOC. */
function kwhForSocTarget(targetPct: number, capacityKwh: number, plugInPct: number): number {
  const gap = Math.max(0, Math.min(100, targetPct) - Math.max(0, Math.min(100, plugInPct)));
  return (gap / 100) * capacityKwh / CHARGE_EFFICIENCY;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Pull a useful message out of an axios/API error. */
function errorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
    if (typeof detail === 'string') return detail;
  }
  return err instanceof Error ? err.message : fallback;
}

/**
 * Session timestamps are stored as naive UTC (no timezone suffix) — append
 * "Z" before parsing so the browser renders them in local time.
 */
function parseUtc(value: string): Date {
  return new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
}

function formatClock(value: string): string {
  return parseUtc(value).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "HH:MM" (local) for a Date — the value a <input type="time"> wants. */
function toTimeInput(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Today's (or, if already past, tomorrow's) local date at "HH:MM", as ISO.
 * A declared end that reads earlier than now is taken to mean tomorrow.
 */
function timeInputToIso(hhmm: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return null;
  const d = new Date();
  d.setHours(Number(m[1]), Number(m[2]), 0, 0);
  if (d.getTime() <= Date.now() + 60_000) d.setDate(d.getDate() + 1);
  return d.toISOString();
}


interface StatusLine {
  label: string;
  chipClass: string;
  dotClass: string;
  pulse: boolean;
}

/** One-line live state: Unplugged / Plugged in / Charging / Paused / Offline. */
function getStatusLine(
  status: EVChargerStatus | null | undefined,
  controlType: EVChargerControlType = 'ocpp',
): StatusLine {
  if (controlType === 'manual') {
    // Nothing to be "offline": the charger never connects. Charging exactly
    // while a declared session is active.
    if (status?.charging && status.expected_end_at) {
      const kw = ((status.declared_power_w ?? status.power_w ?? 0) / 1000).toFixed(1);
      return {
        label: `Charging at ${kw} kW until ${formatClock(status.expected_end_at)}`,
        chipClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        dotClass: 'bg-green-500',
        pulse: true,
      };
    }
    return {
      label: 'Not charging',
      chipClass: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      dotClass: 'bg-gray-400',
      pulse: false,
    };
  }
  if (!status || !status.connected) {
    return {
      label: 'Offline',
      chipClass: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      dotClass: 'bg-gray-400',
      pulse: false,
    };
  }
  if (!status.plugged_in) {
    return {
      label: 'Unplugged',
      chipClass: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      dotClass: 'bg-gray-400',
      pulse: false,
    };
  }
  if (status.charging) {
    // Some chargers never sample a power measurand; the gateway then
    // derives kW from the energy register, which takes a minute or two
    // to settle. Until then say "Charging" rather than "0.0 kW".
    const watts = status.power_w ?? 0;
    const kw = watts > 10 ? ` at ${(watts / 1000).toFixed(1)} kW` : '';
    const source = status.boost
      ? ''
      : status.top_up_active
        ? ' (grid top-up)'
        : status.solar_only
          ? ' from solar'
          : '';
    return {
      label: `Charging${source}${kw}`,
      chipClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      dotClass: 'bg-green-500',
      pulse: true,
    };
  }
  if (status.target_reached) {
    return {
      label: `Target reached${status.target_label ? ` (${status.target_label})` : ''}`,
      chipClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      dotClass: 'bg-emerald-500',
      pulse: false,
    };
  }
  if (status.solar_only && status.charger_manages_solar) {
    return {
      label: 'Plugged in — charger follows solar itself',
      chipClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      dotClass: 'bg-amber-500',
      pulse: false,
    };
  }
  if (status.solar_only) {
    const topUpAt =
      status.grid_top_up && status.next_window_start
        ? new Date(status.next_window_start).toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        : null;
    return {
      label: topUpAt
        ? `Waiting for solar · grid top-up at ${topUpAt}`
        : 'Waiting for solar surplus',
      chipClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      dotClass: 'bg-amber-500',
      pulse: false,
    };
  }
  if (status.suspended_by_plan) {
    const resumes = status.next_window_start
      ? new Date(status.next_window_start).toLocaleTimeString('en-AU', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      : null;
    return {
      label: resumes ? `Paused by plan, resumes ${resumes}` : 'Paused by plan',
      chipClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      dotClass: 'bg-amber-500',
      pulse: false,
    };
  }
  return {
    label: 'Plugged in — waiting',
    chipClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    dotClass: 'bg-blue-500',
    pulse: false,
  };
}

// ---------------------------------------------------------------------------
// Section: fetches the site's chargers, polls status, hosts the add flow
// ---------------------------------------------------------------------------

interface EVChargersSectionProps {
  siteId: string;
}

/**
 * SiteDetail's EV charger section. Renders a full card per provisioned
 * charger (nothing when the site has none) plus the "Add EV Charger"
 * provisioning entry point. Polls charger live status every 20 seconds.
 */
export function EVChargersSection({ siteId }: EVChargersSectionProps) {
  const [chargers, setChargers] = useState<EVCharger[] | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await getEVChargers(siteId);
      setChargers(rows);
    } catch (err) {
      // Not critical — leave whatever was last shown.
      console.error('Failed to fetch EV chargers:', err);
    }
  }, [siteId]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, STATUS_POLL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  // Initial load not done (or first fetch failed) — render nothing rather
  // than flashing an empty add row.
  if (chargers === null) return null;

  return (
    <div className="space-y-4">
      {chargers.map((charger) => (
        <EVChargerCard key={charger.id} siteId={siteId} charger={charger} onChanged={refresh} />
      ))}

      {/* Provisioning entry point — one charger per site (the live status
          key is per site); edit or remove the existing one via its pencil. */}
      {chargers.length === 0 && (
        <button
          onClick={() => setShowAdd(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-brand-orange hover:text-brand-orange transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add EV Charger
        </button>
      )}

      {showAdd && (
        <AddEVChargerModal
          siteId={siteId}
          onClose={() => setShowAdd(false)}
          onCreated={refresh}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-charger card
// ---------------------------------------------------------------------------

interface EVChargerCardProps {
  siteId: string;
  charger: EVCharger;
  onChanged: () => void;
}

export function EVChargerCard({ siteId, charger, onChanged }: EVChargerCardProps) {
  const status = charger.status ?? null;
  const isManual = charger.control_type === 'manual';
  const line = getStatusLine(status, charger.control_type);

  // Manual commands (boost / stop) — the API waits for the gateway ack, so
  // a command can take up to ~8s.
  const [commandBusy, setCommandBusy] = useState<EVChargerCommandName | null>(null);
  const [commandMsg, setCommandMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Charging preferences form. Initialized once per charger (the card is
  // keyed by charger id) so the 20s status poll doesn't clobber typing.
  const prefs = charger.charging_prefs;
  const storedSocMode = prefs?.target_soc_percent != null && prefs?.vehicle_battery_kwh != null;
  const [prefsForm, setPrefsForm] = useState({
    target_mode: (storedSocMode ? 'soc' : 'kwh') as 'kwh' | 'soc',
    target_kwh: prefs?.target_kwh != null ? String(prefs.target_kwh) : '',
    target_soc_percent:
      prefs?.target_soc_percent != null ? String(prefs.target_soc_percent) : '80',
    vehicle_battery_kwh:
      prefs?.vehicle_battery_kwh != null ? String(prefs.vehicle_battery_kwh) : '',
    plug_in_soc_percent:
      prefs?.plug_in_soc_percent != null ? String(prefs.plug_in_soc_percent) : '',
    ready_by: prefs?.ready_by ?? '',
    smart_enabled: prefs?.smart_enabled ?? true,
    solar_only: prefs?.solar_only ?? false,
    charger_manages_solar: prefs?.charger_manages_solar ?? false,
    grid_top_up: prefs?.grid_top_up ?? false,
    manual: prefs?.manual_amps != null && !prefs?.solar_only,
    manual_amps: Math.round(prefs?.manual_amps ?? charger.max_current_a ?? 16),
  });
  // Manual-speed slider range and kW per amp (the car's phases when known).
  const minAmps = Math.max(1, Math.round(charger.min_current_a ?? 6));
  const maxAmps = Math.max(minAmps, Math.round(charger.max_current_a ?? 16));
  const kwPerAmp =
    ((charger.voltage_v ?? 230) * (status?.effective_phases ?? charger.phases ?? 1)) / 1000;
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // "Battery was at __ % when I plugged in" for this plug-in (SOC targets).
  const [plugInSocDraft, setPlugInSocDraft] = useState('');
  const [plugInSocBusy, setPlugInSocBusy] = useState(false);

  const socPrefsValid =
    prefsForm.target_mode === 'soc' &&
    Number.isFinite(parseFloat(prefsForm.target_soc_percent)) &&
    Number.isFinite(parseFloat(prefsForm.vehicle_battery_kwh)) &&
    parseFloat(prefsForm.vehicle_battery_kwh) > 0;
  const socTargetKwh = socPrefsValid
    ? kwhForSocTarget(
        parseFloat(prefsForm.target_soc_percent),
        parseFloat(prefsForm.vehicle_battery_kwh),
        prefsForm.plug_in_soc_percent === '' ? 0 : parseFloat(prefsForm.plug_in_soc_percent),
      )
    : null;

  // Connection settings (lazily fetched — the list endpoint doesn't include them)
  const [showConnection, setShowConnection] = useState(false);
  const [connection, setConnection] = useState<EVChargerConnection | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(false);

  const [showEdit, setShowEdit] = useState(false);

  const handleCommand = async (command: EVChargerCommandName) => {
    if (commandBusy) return;
    setCommandBusy(command);
    setCommandMsg(null);
    try {
      const result = await sendEVChargerCommand(siteId, charger.id, command);
      setCommandMsg({
        ok: result.success,
        text: result.message || (result.success ? 'Command sent' : 'Command failed'),
      });
      if (result.success) onChanged();
    } catch (err) {
      setCommandMsg({ ok: false, text: errorMessage(err, 'Command failed') });
    } finally {
      setCommandBusy(null);
      setTimeout(() => setCommandMsg(null), 8000);
    }
  };

  const handleSavePrefs = async () => {
    if (savingPrefs) return;
    setSavingPrefs(true);
    setPrefsMsg(null);
    try {
      const num = (raw: string): number | null => {
        if (raw === '') return null;
        const v = parseFloat(raw);
        return Number.isFinite(v) ? v : null;
      };
      const socMode = prefsForm.target_mode === 'soc';
      if (socMode && !socPrefsValid) {
        setPrefsMsg({ ok: false, text: 'A % target needs the target % and the battery size' });
        return;
      }
      await updateEVCharger(siteId, charger.id, {
        charging_prefs: {
          // Only the active mode's target is stored so the gateway can't
          // pick up a stale one (the % pair wins over target_kwh).
          target_kwh: socMode ? null : num(prefsForm.target_kwh),
          target_soc_percent: socMode ? num(prefsForm.target_soc_percent) : null,
          vehicle_battery_kwh: num(prefsForm.vehicle_battery_kwh),
          plug_in_soc_percent: socMode ? num(prefsForm.plug_in_soc_percent) : null,
          ready_by: prefsForm.ready_by || null,
          smart_enabled: prefsForm.smart_enabled,
          solar_only: prefsForm.solar_only,
          charger_manages_solar: prefsForm.solar_only && prefsForm.charger_manages_solar,
          grid_top_up:
            prefsForm.solar_only && !prefsForm.charger_manages_solar && prefsForm.grid_top_up,
          manual_amps: !prefsForm.solar_only && prefsForm.manual ? prefsForm.manual_amps : null,
        },
      });
      setPrefsMsg({ ok: true, text: 'Preferences saved' });
      onChanged();
    } catch (err) {
      setPrefsMsg({ ok: false, text: errorMessage(err, 'Failed to save preferences') });
    } finally {
      setSavingPrefs(false);
      setTimeout(() => setPrefsMsg(null), 5000);
    }
  };

  const handleSetPlugInSoc = async (clear = false) => {
    if (plugInSocBusy) return;
    const pct = clear ? null : parseFloat(plugInSocDraft);
    if (!clear && (pct == null || !Number.isFinite(pct) || pct < 0 || pct > 100)) {
      setCommandMsg({ ok: false, text: 'Enter the battery % (0-100)' });
      return;
    }
    setPlugInSocBusy(true);
    setCommandMsg(null);
    try {
      const result = await sendEVChargerCommand(siteId, charger.id, 'set_plug_in_soc', undefined, pct);
      setCommandMsg({ ok: result.success, text: result.message || 'Updated' });
      if (result.success) {
        setPlugInSocDraft('');
        onChanged();
      }
    } catch (err) {
      setCommandMsg({ ok: false, text: errorMessage(err, 'Failed to set plug-in battery %') });
    } finally {
      setPlugInSocBusy(false);
      setTimeout(() => setCommandMsg(null), 8000);
    }
  };

  const toggleConnection = async () => {
    if (showConnection) {
      setShowConnection(false);
      return;
    }
    if (!connection && !connectionLoading) {
      setConnectionLoading(true);
      try {
        const detail = await getEVCharger(siteId, charger.id);
        setConnection(detail.connection ?? null);
      } catch (err) {
        console.error('Failed to fetch charger connection info:', err);
      } finally {
        setConnectionLoading(false);
      }
    }
    setShowConnection(true);
  };

  const boostActive = Boolean(status?.boost);
  const controlsDisabled = !status || !status.connected;

  return (
    <div className="card p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-gray-200 dark:border-gray-600 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2 rounded-lg ${status?.charging ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
            {isManual ? (
              <Car className={`w-6 h-6 ${status?.charging ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
            ) : (
              <PlugZap className={`w-6 h-6 ${status?.charging ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white truncate">{charger.name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {isManual
                ? 'Manual charger · declared sessions'
                : [charger.vendor, charger.model].filter(Boolean).join(' ') || 'EV Charger'}
              {' · '}
              {charger.max_current_a.toFixed(0)}A / {charger.phases}-phase / {(charger.max_power_w / 1000).toFixed(1)} kW max
              {!isManual &&
                status?.vehicle_phases != null &&
                status.vehicle_phases < charger.phases &&
                status.effective_max_power_w != null && (
                  <span
                    title="Learned from the charger's per-phase currents: this car's onboard charger uses fewer phases than the wall box has"
                  >
                    {' · '}car uses {status.vehicle_phases}-phase ({(status.effective_max_power_w / 1000).toFixed(1)} kW)
                  </span>
                )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${line.chipClass}`}>
            <span className={`w-2 h-2 rounded-full ${line.dotClass} ${line.pulse ? 'animate-pulse' : ''}`}></span>
            {line.label}
          </span>
          <button
            onClick={() => setShowEdit(true)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-brand-orange hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Edit charger"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isManual && (
        <ManualSessionPanel
          siteId={siteId}
          charger={charger}
          status={status}
          onChanged={onChanged}
        />
      )}

      {/* Live stats */}
      {!isManual && (() => {
        const deliveredWh = status?.episode_energy_wh ?? status?.session_energy_wh ?? null;
        const targetWh = status?.target_wh ?? null;
        const estSoc = status?.estimated_soc_percent ?? null;
        const socTargetPct = prefs?.target_soc_percent ?? null;
        const powerW = status?.charging ? status.power_w ?? null : status ? 0 : null;
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-brand-navy-light/50">
              <Activity className="w-5 h-5 text-pink-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Power</p>
                <p
                  className="font-medium text-gray-900 dark:text-white text-sm"
                  title={
                    status?.power_source === 'derived'
                      ? 'Charger reports no power reading; derived from its energy meter'
                      : undefined
                  }
                >
                  {powerW == null
                    ? '—'
                    : `${status?.power_source === 'derived' ? '~' : ''}${(powerW / 1000).toFixed(1)} kW`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-brand-navy-light/50">
              <Zap className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">This plug-in</p>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  {deliveredWh == null
                    ? '—'
                    : targetWh != null && targetWh > 0
                      ? `${(deliveredWh / 1000).toFixed(1)} / ${(targetWh / 1000).toFixed(1)} kWh`
                      : `${(deliveredWh / 1000).toFixed(1)} kWh`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-brand-navy-light/50">
              <BatteryCharging className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {status?.soc_percent != null ? 'Car battery' : 'Est. car battery'}
                </p>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  {estSoc == null
                    ? '—'
                    : `${status?.soc_percent != null ? '' : '~'}${Math.round(estSoc)}%${
                        socTargetPct != null ? ` → ${Math.round(socTargetPct)}%` : ''
                      }`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-brand-navy-light/50">
              <PlugZap className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Current Limit</p>
                <p className="font-medium text-gray-900 dark:text-white text-sm">
                  {status?.current_limit_a != null ? `${status.current_limit_a.toFixed(0)} A` : '—'}
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Per-plug-in arrival SOC (only meaningful for a % target) */}
      {!isManual && storedSocMode && status?.plugged_in && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>
            Battery when plugged in:{' '}
            <span className="font-medium text-gray-900 dark:text-white">
              {status.plug_in_soc_percent != null ? `${Math.round(status.plug_in_soc_percent)}%` : 'unknown (assumes empty)'}
            </span>
            {status.plug_in_soc_session != null && ' (set for this plug-in)'}
          </span>
          <div className="flex items-center gap-1 ml-auto">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              placeholder="%"
              value={plugInSocDraft}
              onChange={(e) => setPlugInSocDraft(e.target.value)}
              disabled={controlsDisabled || plugInSocBusy}
              className="w-16 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white"
              title="The car's battery % when you plugged it in this time"
            />
            <button
              onClick={() => handleSetPlugInSoc(false)}
              disabled={controlsDisabled || plugInSocBusy || plugInSocDraft === ''}
              className="px-2 py-1 text-xs font-medium rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 hover:opacity-90 disabled:opacity-50"
            >
              {plugInSocBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Set'}
            </button>
            {status.plug_in_soc_session != null && (
              <button
                onClick={() => handleSetPlugInSoc(true)}
                disabled={controlsDisabled || plugInSocBusy}
                className="px-2 py-1 text-xs rounded-lg text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 disabled:opacity-50"
                title="Go back to the usual plug-in %"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Manual controls */}
      {!isManual && (
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleCommand(boostActive ? 'boost_off' : 'boost_on')}
          disabled={controlsDisabled || commandBusy !== null}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            boostActive
              ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-brand-orange hover:opacity-90 text-white'
          }`}
          title={boostActive ? 'Return to the smart schedule' : 'Charge now at full rate'}
        >
          {commandBusy === 'boost_on' || commandBusy === 'boost_off' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Zap className="w-4 h-4" />
          )}
          {boostActive ? 'Boost On — turn off' : 'Boost'}
        </button>
        <button
          onClick={() => handleCommand('stop')}
          disabled={controlsDisabled || commandBusy !== null || !status?.plugged_in}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-700 dark:text-gray-300 hover:text-red-700 dark:hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="End the charging session"
        >
          {commandBusy === 'stop' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Square className="w-4 h-4" />
          )}
          Stop
        </button>
      </div>
      )}
      {commandMsg && (
        <p className={`text-xs ${commandMsg.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {commandMsg.text}
        </p>
      )}

      {/* Charging preferences (OCPP only — a manual charger can't be scheduled) */}
      {!isManual && (
      <div className="bg-gray-50 dark:bg-brand-navy-light/50 rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-900 dark:text-white">Charging Preferences</span>
          {/* Smart charging toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {prefsForm.smart_enabled ? 'Smart' : 'Charge immediately'}
            </span>
            <Toggle
              size="sm"
              checked={prefsForm.smart_enabled}
              onChange={(next) => setPrefsForm({ ...prefsForm, smart_enabled: next })}
              aria-label="Smart charging"
              title={
                prefsForm.smart_enabled
                  ? 'Smart charging: schedule around prices and solar'
                  : 'Charge immediately at full rate on plug-in'
              }
            />
          </div>
        </div>
        {/* Charge source: price/solar plan vs solar surplus only */}
        {prefsForm.smart_enabled && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Charge from</span>
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
                {(['plan', 'solar', 'manual'] as const).map((src) => {
                  const current = prefsForm.solar_only
                    ? 'solar'
                    : prefsForm.manual
                      ? 'manual'
                      : 'plan';
                  return (
                    <button
                      key={src}
                      type="button"
                      onClick={() =>
                        setPrefsForm({
                          ...prefsForm,
                          solar_only: src === 'solar',
                          manual: src === 'manual',
                        })
                      }
                      className={`px-2.5 py-1 ${
                        current === src
                          ? 'bg-ink text-on-ink'
                          : 'bg-card dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                      title={
                        src === 'solar'
                          ? 'Only ever use power that would otherwise be exported'
                          : src === 'manual'
                            ? 'Charge at a speed you choose'
                            : 'Schedule around prices and the solar forecast'
                      }
                    >
                      {src === 'solar'
                        ? 'Solar surplus only'
                        : src === 'manual'
                          ? 'Manual speed'
                          : 'Cheapest (plan)'}
                    </button>
                  );
                })}
              </div>
            </div>
            {!prefsForm.solar_only && prefsForm.manual && (
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={minAmps}
                    max={maxAmps}
                    step={1}
                    value={Math.min(maxAmps, Math.max(minAmps, prefsForm.manual_amps))}
                    onChange={(e) =>
                      setPrefsForm({ ...prefsForm, manual_amps: Number(e.target.value) })
                    }
                    className="flex-1 accent-brand-orange"
                    aria-label="Charge speed"
                  />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums w-24 text-right">
                    {(prefsForm.manual_amps * kwPerAmp).toFixed(1)} kW
                    <span className="ml-1 text-xs font-normal text-gray-500">
                      {prefsForm.manual_amps} A
                    </span>
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  The car charges at this speed whenever it is plugged in. Stops at the target if
                  one is set; ready-by is ignored. The home battery may help power the car.
                </p>
              </div>
            )}
            {prefsForm.solar_only && (
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefsForm.charger_manages_solar}
                    onChange={(e) =>
                      setPrefsForm({ ...prefsForm, charger_manages_solar: e.target.checked })
                    }
                    className="mt-0.5"
                  />
                  <span>
                    My charger follows solar itself (its own CTs are fitted and its solar mode is on).
                    SolanIQ then sends it no charging limits and only records what it uses.
                  </span>
                </label>
                {!prefsForm.charger_manages_solar && (
                  <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prefsForm.grid_top_up}
                      onChange={(e) => setPrefsForm({ ...prefsForm, grid_top_up: e.target.checked })}
                      className="mt-0.5"
                    />
                    <span>
                      Top up from the grid if solar won’t reach the target by Ready By (cheapest times
                      first). Needs a target and a Ready By time.
                    </span>
                  </label>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {prefsForm.charger_manages_solar
                    ? 'Hands-off: the charger’s own controller decides. Note it only sees the grid, so it may draw from the home battery; the target below is not enforced in this mode.'
                    : prefsForm.grid_top_up
                      ? 'Solar first: the car takes what the site would otherwise export, after the home battery. Whatever is still owed for the target is scheduled from the grid in the cheapest slots before Ready By, so the car is ready on time.'
                      : 'SolanIQ steers the charger to use only what the site is exporting, so the home battery is served first and the car never pulls from the grid. It stops at the target below if one is set; Ready By is ignored in this mode (use Charge now to top up from the grid).'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Target: charge to a battery % or deliver a fixed kWh */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Target</span>
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
            {(['soc', 'kwh'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setPrefsForm({ ...prefsForm, target_mode: mode })}
                className={`px-2.5 py-1 ${
                  prefsForm.target_mode === mode
                    ? 'bg-ink text-on-ink'
                    : 'bg-card dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {mode === 'soc' ? 'Battery %' : 'kWh'}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {prefsForm.target_mode === 'kwh' ? (
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Target Energy (kWh)</label>
              <input
                type="number"
                min={0}
                max={200}
                step={1}
                placeholder="Full charge"
                value={prefsForm.target_kwh}
                onChange={(e) => setPrefsForm({ ...prefsForm, target_kwh: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
              />
            </div>
          ) : (
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Charge to (%)</label>
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                value={prefsForm.target_soc_percent}
                onChange={(e) => setPrefsForm({ ...prefsForm, target_soc_percent: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Ready By</label>
            <input
              type="time"
              value={prefsForm.ready_by}
              onChange={(e) => setPrefsForm({ ...prefsForm, ready_by: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
          </div>
        </div>
        {prefsForm.target_mode === 'soc' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Car battery (kWh)</label>
                <input
                  type="number"
                  min={1}
                  max={300}
                  step={0.5}
                  placeholder="e.g. 60"
                  value={prefsForm.vehicle_battery_kwh}
                  onChange={(e) => setPrefsForm({ ...prefsForm, vehicle_battery_kwh: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Usually plug in at (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  placeholder="Empty"
                  value={prefsForm.plug_in_soc_percent}
                  onChange={(e) => setPrefsForm({ ...prefsForm, plug_in_soc_percent: e.target.value })}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                />
              </div>
            </div>
            <select
              value=""
              onChange={(e) => {
                const preset = EV_BATTERY_PRESETS.find((p) => p.label === e.target.value);
                if (preset) setPrefsForm({ ...prefsForm, vehicle_battery_kwh: String(preset.kwh) });
              }}
              className="w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-600 dark:text-gray-300"
              title="Approximate usable battery sizes; check your car's specs if unsure"
            >
              <option value="">Not sure of the battery size? Pick your car…</option>
              {EV_BATTERY_PRESETS.map((p) => (
                <option key={p.label} value={p.label}>
                  {p.label} — ~{p.kwh} kWh
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {socTargetKwh != null
                ? `≈ ${socTargetKwh.toFixed(1)} kWh per plug-in from ${
                    prefsForm.plug_in_soc_percent === '' ? 'empty' : `${prefsForm.plug_in_soc_percent}%`
                  } to ${prefsForm.target_soc_percent}% (allowing ~10% charging losses). Charging stops at the target; use Boost to add more.`
                : 'Enter the target % and the car battery size. The charger cannot read the car’s battery, so the starting point is the % you give; you can set it per plug-in on the card.'}
            </p>
          </div>
        )}
        <div className="flex items-center justify-between">
          {prefsMsg ? (
            <p className={`text-xs ${prefsMsg.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {prefsMsg.text}
            </p>
          ) : (
            <span />
          )}
          <button
            onClick={handleSavePrefs}
            disabled={savingPrefs}
            className="px-3 py-1.5 text-sm font-medium text-white bg-brand-orange hover:opacity-90 rounded-full disabled:opacity-50 flex items-center gap-1"
          >
            {savingPrefs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </button>
        </div>
      </div>
      )}

      {/* Connection settings (collapsible, OCPP only) */}
      {!isManual && (
      <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
        <button
          onClick={toggleConnection}
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          {connectionLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : showConnection ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
          Connection settings
        </button>
        {showConnection && connection && (
          <div className="mt-3">
            <ConnectionPanel connection={connection} />
          </div>
        )}
      </div>
      )}

      {showEdit && (
        <EditEVChargerModal
          siteId={siteId}
          charger={charger}
          onClose={() => setShowEdit(false)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection info panel (setup values + copy buttons)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Manual charger: declare / extend / end a charging session
// ---------------------------------------------------------------------------

interface ManualSessionPanelProps {
  siteId: string;
  charger: EVCharger;
  status: EVChargerStatus | null;
  onChanged: () => void;
}

const DEFAULT_SESSION_HOURS = 3;

/**
 * For chargers SolanIQ can't talk to. Declaring "charging until HH:MM at
 * X kW" puts the window into the battery plan as a fixed load, so the
 * optimizer can e.g. grid-charge the home battery while import is cheap
 * instead of assuming solar will refill it. Energy shown is estimated from
 * site load above the usual house baseline.
 */
function ManualSessionPanel({ siteId, charger, status, onChanged }: ManualSessionPanelProps) {
  const active = charger.active_session ?? null;
  const maxKw = charger.max_power_w / 1000;

  const defaultEnd = () => {
    const d = new Date(Date.now() + DEFAULT_SESSION_HOURS * 3600_000);
    d.setMinutes(Math.round(d.getMinutes() / 15) * 15, 0, 0);
    return toTimeInput(d);
  };
  const [endTime, setEndTime] = useState(defaultEnd);
  const [powerKw, setPowerKw] = useState(maxKw.toFixed(1));
  const [editingEnd, setEditingEnd] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const flash = (ok: boolean, text: string) => {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 6000);
  };

  const run = async (key: string, fn: () => Promise<unknown>, okText: string) => {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      flash(true, okText);
      onChanged();
    } catch (err) {
      flash(false, errorMessage(err, 'Request failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleStart = () => {
    const until = timeInputToIso(endTime);
    if (!until) {
      flash(false, 'Enter an end time');
      return;
    }
    const kw = parseFloat(powerKw);
    run(
      'start',
      () =>
        startEVManualSession(siteId, charger.id, {
          until,
          power_w: Number.isFinite(kw) && kw > 0 ? Math.round(kw * 1000) : undefined,
        }),
      'Charging declared — battery plan is being updated',
    );
  };

  const handleExtend = (minutes: number) => {
    if (!active?.expected_end_at) return;
    const base = Math.max(parseUtc(active.expected_end_at).getTime(), Date.now());
    const until = new Date(base + minutes * 60_000).toISOString();
    run('extend', () => updateEVManualSession(siteId, charger.id, active.id, { until }), 'Session extended');
  };

  const handleRetime = () => {
    if (!active) return;
    const until = timeInputToIso(endTime);
    if (!until) {
      flash(false, 'Enter an end time');
      return;
    }
    run('retime', () => updateEVManualSession(siteId, charger.id, active.id, { until }), 'End time updated').then(
      () => setEditingEnd(false),
    );
  };

  const handleEnd = () => {
    if (!active) return;
    run('end', () => endEVManualSession(siteId, charger.id, active.id), 'Session ended');
  };

  const inputClass =
    'w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent';

  if (active) {
    const estKwh = ((status?.session_energy_wh ?? active.energy_wh) / 1000).toFixed(1);
    const declaredKw = ((active.declared_power_w ?? charger.max_power_w) / 1000).toFixed(1);
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">EV charging declared</p>
            <p className="text-xs text-green-700 dark:text-green-400">
              {declaredKw} kW until{' '}
              {active.expected_end_at ? formatClock(active.expected_end_at) : '—'} · ~{estKwh} kWh so far
            </p>
          </div>
          <Clock className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
        </div>

        {editingEnd ? (
          <div className="flex items-center gap-2">
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputClass} />
            <button
              onClick={handleRetime}
              disabled={busy !== null}
              className="px-3 py-1.5 text-sm font-medium text-white bg-brand-orange hover:opacity-90 rounded-full disabled:opacity-50 whitespace-nowrap"
            >
              {busy === 'retime' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set'}
            </button>
            <button
              onClick={() => setEditingEnd(false)}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => handleExtend(60)}
              disabled={busy !== null}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-card dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-brand-orange disabled:opacity-50"
            >
              {busy === 'extend' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              1 hour
            </button>
            <button
              onClick={() => {
                if (active.expected_end_at) setEndTime(toTimeInput(parseUtc(active.expected_end_at)));
                setEditingEnd(true);
              }}
              disabled={busy !== null}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-card dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-brand-orange disabled:opacity-50"
            >
              Change end time
            </button>
            <button
              onClick={handleEnd}
              disabled={busy !== null}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-700 dark:text-gray-300 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-50"
              title="The car stopped charging — end the declared session now"
            >
              {busy === 'end' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
              Stopped charging
            </button>
          </div>
        )}
        {msg && (
          <p className={`text-xs ${msg.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {msg.text}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-brand-navy-light/50 rounded-lg p-3 space-y-3">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">Charging your EV?</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Tell SolanIQ so the battery plan allows for it — e.g. topping up the home battery
          from the grid while import is cheap instead of waiting for solar the car will use.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Charging until</label>
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Power (kW)</label>
          <input
            type="number"
            min={0.5}
            max={Math.ceil(maxKw * 1.1)}
            step={0.1}
            value={powerKw}
            onChange={(e) => setPowerKw(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        {msg ? (
          <p className={`text-xs ${msg.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {msg.text}
          </p>
        ) : (
          <span className="text-xs text-gray-400">Ends automatically at the time you set; you'll get a nudge to extend.</span>
        )}
        <button
          onClick={handleStart}
          disabled={busy !== null}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-orange hover:opacity-90 rounded-full disabled:opacity-50 whitespace-nowrap"
        >
          {busy === 'start' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />}
          I'm charging now
        </button>
      </div>
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code className="flex-1 px-2 py-1.5 text-xs font-mono bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white break-all">
          {value}
        </code>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-brand-orange hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={`Copy ${label.toLowerCase()}`}
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function ConnectionPanel({ connection }: { connection: EVChargerConnection }) {
  return (
    <div className="space-y-3">
      <CopyField label="OCPP WebSocket URL" value={connection.ws_url} />
      <CopyField label="Charge Point ID" value={connection.charge_point_id} />
      {connection.auth_password && (
        <CopyField label="Authorization Password" value={connection.auth_password} />
      )}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Enter these values in your charger's OCPP settings. The authorization
        password is only needed during setup.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add EV Charger modal (provisioning flow: form -> success panel)
// ---------------------------------------------------------------------------

const PHASE_OPTIONS = [
  { value: 1, label: 'Single-phase' },
  { value: 3, label: 'Three-phase' },
];

interface AddEVChargerModalProps {
  siteId: string;
  onClose: () => void;
  onCreated: () => void;
}

export function AddEVChargerModal({ siteId, onClose, onCreated }: AddEVChargerModalProps) {
  const [form, setForm] = useState({
    name: 'EV Charger',
    control_type: 'ocpp' as EVChargerControlType,
    max_current_a: '32',
    phases: 1,
    voltage_v: '230',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once provisioning succeeds — switches the modal to the success panel.
  const [created, setCreated] = useState<EVCharger | null>(null);

  const handleCreate = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const maxCurrent = parseFloat(form.max_current_a);
      const voltage = parseFloat(form.voltage_v);
      const charger = await createEVCharger(siteId, {
        name: form.name.trim() || 'EV Charger',
        control_type: form.control_type,
        max_current_a: Number.isFinite(maxCurrent) ? maxCurrent : 32,
        phases: form.phases,
        voltage_v: Number.isFinite(voltage) ? voltage : 230,
      });
      setCreated(charger);
      onCreated();
    } catch (err) {
      setError(errorMessage(err, 'Failed to provision charger'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="card shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {created ? (created.control_type === 'manual' ? 'Charger Added' : 'Charger Provisioned') : 'Add EV Charger'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {created ? (
          // Success panel: setup values for the charger's OCPP settings screen.
          <>
            <div className="px-6 py-4 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                {created.control_type === 'manual' ? (
                  <p className="text-sm text-green-800 dark:text-green-300">
                    <span className="font-medium">{created.name}</span> has been added. Whenever
                    the car is charging, use <span className="font-medium">I'm charging now</span> on
                    its card so the battery plan allows for the extra load.
                  </p>
                ) : (
                  <p className="text-sm text-green-800 dark:text-green-300">
                    <span className="font-medium">{created.name}</span> is ready to connect.
                    Enter the values below in your charger's OCPP settings — the
                    authorization password is only shown during setup, so copy it now.
                  </p>
                )}
              </div>
              {created.connection && <ConnectionPanel connection={created.connection} />}
            </div>
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:opacity-90 rounded-full"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <form
              className="px-6 py-4 space-y-4"
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Charger type</label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    {
                      value: 'ocpp' as EVChargerControlType,
                      title: 'Smart charger (OCPP)',
                      blurb: 'SolanIQ schedules and meters the charging',
                      icon: PlugZap,
                    },
                    {
                      value: 'manual' as EVChargerControlType,
                      title: 'Other charger',
                      blurb: "No smart control — you tell SolanIQ when you're charging",
                      icon: Car,
                    },
                  ]).map((opt) => {
                    const selected = form.control_type === opt.value;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm({ ...form, control_type: opt.value })}
                        className={`text-left p-3 rounded-lg border transition-colors ${
                          selected
                            ? 'border-brand-orange bg-orange-50 dark:bg-brand-orange/10'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                          <Icon className="w-4 h-4" />
                          {opt.title}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.blurb}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Current (A)</label>
                  <input
                    type="number"
                    min={1}
                    max={80}
                    step={1}
                    value={form.max_current_a}
                    onChange={(e) => setForm({ ...form, max_current_a: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phases</label>
                  <select
                    value={form.phases}
                    onChange={(e) => setForm({ ...form, phases: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  >
                    {PHASE_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Voltage (V)</label>
                  <input
                    type="number"
                    min={100}
                    max={440}
                    step={1}
                    value={form.voltage_v}
                    onChange={(e) => setForm({ ...form, voltage_v: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Use the current rating of the charger's circuit. Most home
                chargers are 32A single-phase (7.4 kW); an 11 kW charger is 16A three-phase.
                {form.control_type === 'manual' && ' This becomes the default power for declared sessions.'}
              </p>
            </form>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:opacity-90 rounded-full disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Charger'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Edit charger modal (name + electrical limits, delete with confirm)
// ---------------------------------------------------------------------------

interface EditEVChargerModalProps {
  siteId: string;
  charger: EVCharger;
  onClose: () => void;
  onChanged: () => void;
}

function EditEVChargerModal({ siteId, charger, onClose, onChanged }: EditEVChargerModalProps) {
  const [form, setForm] = useState({
    name: charger.name,
    max_current_a: String(charger.max_current_a),
    min_current_a: String(charger.min_current_a),
    phases: charger.phases,
    voltage_v: String(charger.voltage_v),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Two-step delete: first click arms, second click deletes.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    if (saving || deleting) return;
    setSaving(true);
    setError(null);
    try {
      const maxCurrent = parseFloat(form.max_current_a);
      const minCurrent = parseFloat(form.min_current_a);
      const voltage = parseFloat(form.voltage_v);
      if (!Number.isFinite(maxCurrent) || !Number.isFinite(minCurrent) || !Number.isFinite(voltage)) {
        setError('Current and voltage values must be numbers');
        setSaving(false);
        return;
      }
      if (minCurrent > maxCurrent) {
        setError('Min current must not exceed max current');
        setSaving(false);
        return;
      }
      await updateEVCharger(siteId, charger.id, {
        name: form.name.trim() || charger.name,
        max_current_a: maxCurrent,
        min_current_a: minCurrent,
        phases: form.phases,
        voltage_v: voltage,
      });
      onChanged();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save charger'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || saving) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteEVCharger(siteId, charger.id);
      onChanged();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to delete charger'));
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="card shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Edit EV Charger</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          className="px-6 py-4 space-y-4"
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Current (A)</label>
              <input
                type="number"
                min={1}
                max={80}
                step={1}
                value={form.max_current_a}
                onChange={(e) => setForm({ ...form, max_current_a: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Current (A)</label>
              <input
                type="number"
                min={1}
                max={32}
                step={1}
                value={form.min_current_a}
                onChange={(e) => setForm({ ...form, min_current_a: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
              />
              <p className="mt-1 text-[11px] text-gray-400">Below this the EV pauses (IEC 61851 floor is 6A).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phases</label>
              <select
                value={form.phases}
                onChange={(e) => setForm({ ...form, phases: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
              >
                {PHASE_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Voltage (V)</label>
              <input
                type="number"
                min={100}
                max={440}
                step={1}
                value={form.voltage_v}
                onChange={(e) => setForm({ ...form, voltage_v: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleDelete}
            disabled={deleting || saving}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
              confirmingDelete
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
            }`}
          >
            {deleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            {confirmingDelete ? 'Confirm delete' : 'Delete'}
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || deleting}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:opacity-90 rounded-full disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
